/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Fragment, cloneElement, useContext } from 'react';
import PropTypes from "prop-types";
import { Draggable } from 'react-beautiful-dnd';
import classNames from 'classnames';
import { EuiDroppableContext } from './droppable';
var spacingToClassNameMap = {
  none: null,
  s: 'euiDraggable--s',
  m: 'euiDraggable--m',
  l: 'euiDraggable--l'
};
export var EuiDraggable = ({
  customDragHandle = false,
  draggableId,
  isDragDisabled = false,
  hasInteractiveChildren = false,
  isRemovable = false,
  index,
  children,
  className,
  spacing = 'none',
  style,
  'data-test-subj': dataTestSubj = 'draggable',
  ...rest
}) => {
  const {
    cloneItems
  } = useContext(EuiDroppableContext);
  return <Draggable draggableId={draggableId} index={index} isDragDisabled={isDragDisabled} {...rest}>
      {(provided, snapshot, rubric) => {
      const classes = classNames('euiDraggable', {
        'euiDraggable--hasClone': cloneItems,
        'euiDraggable--hasCustomDragHandle': customDragHandle,
        'euiDraggable--isDragging': snapshot.isDragging,
        'euiDraggable--withoutDropAnimation': isRemovable
      }, spacingToClassNameMap[spacing], className);
      const childClasses = classNames('euiDraggable__item', {
        'euiDraggable__item--hasCustomDragHandle': customDragHandle,
        'euiDraggable__item--isDisabled': isDragDisabled,
        'euiDraggable__item--isDragging': snapshot.isDragging,
        'euiDraggable__item--isDropAnimating': snapshot.isDropAnimating
      });
      const DraggableElement = typeof children === 'function' ? children(provided, snapshot, rubric) : children; // as specified by `DraggableProps`
      return <Fragment>
            <div {...provided.draggableProps} {...!customDragHandle ? provided.dragHandleProps : {}} ref={provided.innerRef} data-test-subj={dataTestSubj} className={classes} style={{
          ...style,
          ...provided.draggableProps.style
        }}
        // We use [role="group"] instead of [role="button"] when we expect a nested
        // interactive element. Screen readers will cue users that this is a container
        // and has one or more elements inside that are part of a related group.
        role={hasInteractiveChildren ? 'group' : provided.dragHandleProps?.role}
        // If the container includes an interactive element, we remove the tabindex=0
        // because [role="group"] does not permit or warrant a tab stop
        tabIndex={hasInteractiveChildren ? undefined : provided.dragHandleProps?.tabIndex}>
              {cloneElement(DraggableElement, {
            className: classNames(DraggableElement.props.className, childClasses)
          })}
            </div>
            {cloneItems && snapshot.isDragging && <div className={classNames(classes, 'euiDraggable--clone')}>
                {DraggableElement}
              </div>}
          </Fragment>;
    }}
    </Draggable>;
};
EuiDraggable.propTypes = {
  /**
     * ReactNode to render as this component's content
     */
  children: PropTypes.oneOfType([PropTypes.element.isRequired, PropTypes.any.isRequired]).isRequired,
  className: PropTypes.string,
  /**
     * Whether the `children` will provide and set up its own drag handle
     */
  customDragHandle: PropTypes.bool,
  /**
     * Whether the container has interactive children and should have `role="group"` instead of `"button"`.
     * Setting this flag ensures your drag & drop container is keyboard and screen reader accessible.
     */
  hasInteractiveChildren: PropTypes.bool,
  /**
     * Whether the item is currently in a position to be removed
     */
  isRemovable: PropTypes.bool,
  /**
     * Adds padding to the draggable item
     */
  spacing: PropTypes.oneOf(["none", "s", "m", "l"]),
  style: PropTypes.any,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any
};
try {
  EuiDraggable.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/drag_and_drop/draggable.tsx',
    description: '',
    displayName: 'EuiDraggable',
    methods: [],
    props: {
      children: {
        defaultValue: null,
        description: "ReactNode to render as this component's content",
        name: 'children',
        parent: {
          fileName: 'app/src/components/drag_and_drop/draggable.tsx',
          name: 'EuiDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/drag_and_drop/draggable.tsx',
          name: 'EuiDraggableProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'ReactElement | DraggableChildrenFn'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/drag_and_drop/draggable.tsx',
          name: 'EuiDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/drag_and_drop/draggable.tsx',
          name: 'EuiDraggableProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      customDragHandle: {
        defaultValue: {
          value: 'false'
        },
        description: 'Whether the `children` will provide and set up its own drag handle',
        name: 'customDragHandle',
        parent: {
          fileName: 'app/src/components/drag_and_drop/draggable.tsx',
          name: 'EuiDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/drag_and_drop/draggable.tsx',
          name: 'EuiDraggableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      hasInteractiveChildren: {
        defaultValue: {
          value: 'false'
        },
        description: 'Whether the container has interactive children and should have `role="group"` instead of `"button"`.\n' + 'Setting this flag ensures your drag & drop container is keyboard and screen reader accessible.',
        name: 'hasInteractiveChildren',
        parent: {
          fileName: 'app/src/components/drag_and_drop/draggable.tsx',
          name: 'EuiDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/drag_and_drop/draggable.tsx',
          name: 'EuiDraggableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isRemovable: {
        defaultValue: {
          value: 'false'
        },
        description: 'Whether the item is currently in a position to be removed',
        name: 'isRemovable',
        parent: {
          fileName: 'app/src/components/drag_and_drop/draggable.tsx',
          name: 'EuiDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/drag_and_drop/draggable.tsx',
          name: 'EuiDraggableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      spacing: {
        defaultValue: {
          value: 'none'
        },
        description: 'Adds padding to the draggable item',
        name: 'spacing',
        parent: {
          fileName: 'app/src/components/drag_and_drop/draggable.tsx',
          name: 'EuiDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/drag_and_drop/draggable.tsx',
          name: 'EuiDraggableProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"s" | "none" | "m" | "l"',
          value: [{
            value: '"s"'
          }, {
            value: '"none"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }]
        }
      },
      style: {
        defaultValue: null,
        description: '',
        name: 'style',
        parent: {
          fileName: 'app/src/components/drag_and_drop/draggable.tsx',
          name: 'EuiDraggableProps'
        },
        declarations: [{
          fileName: 'app/src/components/drag_and_drop/draggable.tsx',
          name: 'EuiDraggableProps'
        }],
        required: false,
        type: {
          name: 'CSSProperties'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      draggableId: {
        defaultValue: null,
        description: '',
        name: 'draggableId',
        parent: {
          fileName: 'app/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DraggableProps'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DraggableProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      index: {
        defaultValue: null,
        description: '',
        name: 'index',
        parent: {
          fileName: 'app/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DraggableProps'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DraggableProps'
        }],
        required: true,
        type: {
          name: 'number'
        }
      },
      isDragDisabled: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'isDragDisabled',
        parent: {
          fileName: 'app/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DraggableProps'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DraggableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      disableInteractiveElementBlocking: {
        defaultValue: null,
        description: '',
        name: 'disableInteractiveElementBlocking',
        parent: {
          fileName: 'app/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DraggableProps'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DraggableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      shouldRespectForcePress: {
        defaultValue: null,
        description: '',
        name: 'shouldRespectForcePress',
        parent: {
          fileName: 'app/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DraggableProps'
        },
        declarations: [{
          fileName: 'app/node_modules/@types/react-beautiful-dnd/index.d.ts',
          name: 'DraggableProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['EuiDraggableProps', 'CommonProps']
  };
} catch (e) {}